
import { useEffect, useState } from 'react';
import { getContacts, getDonations, getImpactStories, getVolunteers } from '../../../api/apiServices';


export default function OverviewSection({ onNavigate }) {
  const [donations, setDonations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
      fetchDonations();
      fetchApplications();
      fetchStories();
      fetchMessages();
    }, []);
  
    const fetchDonations = async () => {
      try {
        const res = await getDonations();
        setDonations(res.data);
      } catch (error) {
        console.error("Failed to fetch donations", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchApplications = async () => {
        try {
          const res = await getVolunteers();
          setVolunteers(res.data);
        } catch (error) {
          console.error("Failed to fetch volunteers", error);
        } finally {
          setLoading(false);
        }
      };

      const fetchStories = async () => {
          try {
            setLoading(true);
            const res = await getImpactStories();
            setStories(res.data || []);
          } catch (err) {
            console.error("Failed to load stories", err);
          } finally {
            setLoading(false);
          }
        };

        const fetchMessages = async () => {
            try {
              const res = await getContacts();
              setMessages(res.data);
            } catch (error) {
              console.error("Failed to fetch contacts", error);
            } finally {
              setLoading(false);
            }
          };

  const stats = [
    { label: 'Total Volunteers', value: volunteers.length, icon: 'ri-pages-line', color: 'bg-[#6a4bc3]' },
    { label: 'Impact Stories', value: stories.length, icon: 'ri-book-open-line', color: 'bg-[#c9a24d]' },
    { label: 'Messages', value: messages.length, icon: 'ri-chat-quote-line', color: 'bg-emerald-500' },
    { label: 'Donations', value: donations.length, icon: 'ri-team-line', color: 'bg-rose-500' },
  ];

  const quickActions = [
    { label: 'Edit Hero Section', icon: 'ri-image-edit-line', section: 'hero' },
    { label: 'Add New Story', icon: 'ri-add-circle-line', section: 'stories' },
    { label: 'Update Statistics', icon: 'ri-bar-chart-line', section: 'statistics' },
    { label: 'Site Settings', icon: 'ri-settings-3-line', section: 'site-settings' },
  ];

  const recentActivity = [
    { action: 'Hero section updated', time: '2 hours ago', icon: 'ri-image-line' },
    { action: 'New story published', time: '5 hours ago', icon: 'ri-book-open-line' },
    { action: 'Team member added', time: '1 day ago', icon: 'ri-user-add-line' },
    { action: 'Statistics updated', time: '2 days ago', icon: 'ri-bar-chart-line' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Dashboard Overview</h2>
        <p className="text-gray-600 text-sm">Manage all your website content from one place</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
              <i className={`${stat.icon} text-white text-lg`}></i>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => onNavigate(action.section)}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-[#6a4bc3]/10 transition-colors cursor-pointer text-left"
              >
                <div className="w-10 h-10 bg-[#6a4bc3]/10 rounded-lg flex items-center justify-center">
                  <i className={`${action.icon} text-[#6a4bc3] text-lg`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        {/* <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className={`${activity.icon} text-gray-600`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </div>

      {/* Content Preview */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Published Stories</h3>
          <button 
            onClick={() => onNavigate('stories')}
            className="text-sm text-[#6a4bc3] font-medium hover:underline cursor-pointer"
          >
            View All
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {stories.slice(0, 3).map((story) => (
            <div key={story.id} className="group cursor-pointer" onClick={() => onNavigate('stories')}>
              <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                <img 
                  src={story.image} 
                  alt={story.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <p className="text-sm font-medium text-gray-900 line-clamp-2">{story.title}</p>
              <span className="text-xs text-[#c9a24d]">{story.category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
